import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import {
  Button,
  Tag,
  Progress,
  Upload,
  Modal,
  Spin,
  Collapse,
  Tabs,
  Tooltip,
  Input,
  Select,
  InputNumber,
  Checkbox,
  Radio,
} from "antd";
import {
  UploadOutlined,
  CheckCircleFilled,
  ClockCircleOutlined,
  EyeOutlined,
  DownloadOutlined,
  LockOutlined,
  FileTextOutlined,
  AuditOutlined,
  BarChartOutlined,
  UserOutlined,
  BellOutlined,
  SwapOutlined,
  LinkedinOutlined,
  SearchOutlined,
  HistoryOutlined,
} from "@ant-design/icons";
import { Severty, ShowToast } from "../helper/toast";
import apiPath from "../constants/apiPath";

const { Panel } = Collapse;

// Human-readable labels for Onboarding.auditLog entries — kept in sync with
// brand-manager-Imergey/src/pages/onboarding/BrandOnboarding.js. Many action
// strings carry a dynamic "_<taskKey>" suffix (e.g. "brand_task_submitted_
// linkedinAudit") — parseAuditAction() strips that off and resolves it to the
// task's display name so the trail reads as "Submitted LinkedIn Audit"
// instead of the raw action string.
const AUDIT_ACTION_LABELS = {
  onboarding_started: "Started onboarding",
  notification_resent: "Sent a reminder to the client",
  step_completed: "Completed a step",
  onboarding_completed: "Completed onboarding",
  brand_task_completion_requested: "Requested completion from the brand team",
  brand_onboarding_completed: "Completed all brand tasks",
  manual_client_completed: "Manually completed client steps",
  status_overridden: "Manually overrode status",
  onboarding_gate_enabled_on_contract: "Enabled onboarding gate on contract creation",
};

// Verb prefixes for the dynamic "<verb>_<taskKey>" actions.
const AUDIT_TASK_VERB_PREFIXES = [
  { prefix: "manual_brand_task_uploaded_", verb: "Manually uploaded" },
  { prefix: "manual_brand_task_requested_", verb: "Requested completion for" },
  { prefix: "brand_task_requested_", verb: "Requested completion for" },
  { prefix: "brand_task_submitted_", verb: "Submitted" },
  { prefix: "brand_task_assigned_", verb: "Assigned" },
  { prefix: "brand_task_unassigned_", verb: "Unassigned" },
];

const AUDIT_ROLE_LABELS = {
  admin: "Admin",
  client: "Client",
  brand: "Brand team",
  system: "System",
};

// Resolves a raw auditLog action string into a readable, task-aware label.
const describeAuditAction = (action) => {
  for (const { prefix, verb } of AUDIT_TASK_VERB_PREFIXES) {
    if (action.startsWith(prefix)) {
      const taskKey = action.slice(prefix.length);
      return `${verb} ${TASK_LABELS[taskKey] || taskKey}`;
    }
  }
  return AUDIT_ACTION_LABELS[action] || action;
};

const AuditTrail = ({ auditLog }) => {
  const entries = [...(auditLog || [])].sort(
    (a, b) => new Date(b.timestamp) - new Date(a.timestamp)
  );
  if (entries.length === 0) {
    return (
      <div style={{ textAlign: "center", padding: "24px 0", color: "#bbb", fontSize: 13 }}>
        No activity recorded yet.
      </div>
    );
  }
  return (
    <div>
      {entries.map((e, idx) => {
        const roleLabel = AUDIT_ROLE_LABELS[e.performedByType] || e.performedByType;
        const displayName = e.performedByName || roleLabel;
        const designation = e.performedByName
          ? (e.performedByDesignation || roleLabel)
          : null;
        return (
          <div
            key={idx}
            style={{
              display: "flex", gap: 12, alignItems: "flex-start",
              padding: "12px 0",
              borderBottom: idx < entries.length - 1 ? "1px solid #f0f0f0" : "none",
            }}
          >
            <Avatar name={displayName} size={32} bg="#185250" />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "baseline", gap: 6, flexWrap: "wrap" }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: "#1a1a1a" }}>{displayName}</span>
                {designation && (
                  <span style={{ fontSize: 11, color: "#aaa" }}>· {designation}</span>
                )}
              </div>
              <div style={{ fontSize: 13, color: "#333", marginTop: 2 }}>
                {describeAuditAction(e.action)}
              </div>
              <div style={{ fontSize: 11, color: "#aaa", marginTop: 3 }}>
                {new Date(e.timestamp).toLocaleString("en-GB", {
                  day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
                })}
                {e.notes && <> · {e.notes}</>}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Kept in sync with brand-manager-Imergey/src/pages/onboarding/BrandOnboarding.js —
// same CSS/markup, only the API endpoints differ (Admin acts on behalf of any
// client, so canSubmit is always true and there's no per-BM "my clients" scoping).
const QUESTION_MAP = {
  q1: "What is your current title, and how long have you held that title?",
  q2: "What do you hope to get out of Personal Branding?",
  q3: "What are the three most important global trends on your mind?",
  q4: "What are the top three Tech trends in your industry on your mind?",
  q5: "What are three topics or aspects that you would like to highlight on your profile?",
  q6: "Are there any organizations, certifications, or professional networks you are part of that you'd like to highlight?",
  q7: "What are the top three social causes you care about?",
  q8: "Choose 3 words your co-workers would define you as.",
  q9: "What are the topics you can talk endlessly about?",
  q10: "If you were to receive an award, what would it be for?",
  q11: "For you, what are 3 core values in an executive leader?",
  q12: "What have been the biggest hurdles in developing your personal brand?",
  q13: "What tone and style do you prefer for your posts?",
  q14: "What two steps could you immediately take to make the biggest difference in your social media popularity?",
  q15: "What are you most proud of professionally?",
  q16: "Think about one or two people who really inspire you — describe them in a word.",
  q17: "What are some hobbies and interests outside of the office that you do?",
  q18: "What is the compliment or acknowledgement you hear most often about yourself?",
  q19: "What activities/efforts do you do offline that help you in the office?",
  q20: "What are some of your favourite books?",
  q21: "What, according to you, are the key success factors of a great personal brand?",
  q22: "What will make this Personal Branding relationship rewarding for you?",
  q23: "What else would you like us to know?",
};

const BASE_URL = apiPath.baseURL;
const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("admin_token")}`,
});

const TASK_LABELS = {
  pbStrategyDeck: "PB Strategy Deck",
  linkedinAudit: "LinkedIn Audit",
  contentStrategyDeck: "Content Strategy Deck",
};
const TASK_ICONS = {
  pbStrategyDeck: <BarChartOutlined />,
  linkedinAudit: <LinkedinOutlined />,
  contentStrategyDeck: <FileTextOutlined />,
};
const TASK_DESCRIPTIONS = {
  pbStrategyDeck: "Personal branding strategy document for the client.",
  linkedinAudit: "Audit of the client's current LinkedIn presence.",
  contentStrategyDeck: "Content plan and strategy for the client's posts.",
};
const TASK_KEYS = ["pbStrategyDeck", "linkedinAudit", "contentStrategyDeck"];
const CLIENT_STEP_ORDER = ["linkedinLogin", "basicInfo", "questionnaire", "resumeUpload"];

const joinList = (v) => (Array.isArray(v) ? v.filter(Boolean).join(", ") : v);

// ─── Avatar / StatusPill / CountdownTimer ──────────────────────────────────
const Avatar = ({ name, image, size = 48, bg = "#185250" }) => {
  if (image) {
    return (
      <img
        src={image}
        alt={name}
        style={{ width: size, height: size, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }}
      />
    );
  }
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%",
      background: bg, color: "#fff",
      display: "flex", alignItems: "center", justifyContent: "center",
      fontWeight: 600, fontSize: size * 0.38, flexShrink: 0,
    }}>
      {(name || "?")[0].toUpperCase()}
    </div>
  );
};

const StatusPill = ({ status }) => {
  const map = {
    completed: { bg: "#e6f4ea", color: "#1a7a3c", label: "Completed" },
    in_progress: { bg: "#e8f4fd", color: "#1565c0", label: "In Progress" },
    not_started: { bg: "#f5f5f5", color: "#888", label: "Not Started" },
    overdue: { bg: "#fdecea", color: "#c62828", label: "Overdue" },
    pending: { bg: "#f5f5f5", color: "#888", label: "Pending" },
  };
  const s = map[status] || map.pending;
  return (
    <span style={{
      display: "inline-block", padding: "3px 10px", borderRadius: 20,
      background: s.bg, color: s.color, fontSize: 12, fontWeight: 600,
    }}>
      {s.label}
    </span>
  );
};

const CountdownTimer = ({ deadline }) => {
  const [remaining, setRemaining] = useState(0);

  useEffect(() => {
    const tick = () => {
      const ms = deadline ? Math.max(0, new Date(deadline).getTime() - Date.now()) : 0;
      setRemaining(ms);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [deadline]);

  if (!deadline) return null;

  const h = Math.floor(remaining / 3600000);
  const m = Math.floor((remaining % 3600000) / 60000);
  const s = Math.floor((remaining % 60000) / 1000);
  const isUrgent = remaining > 0 && remaining < 6 * 3600000;
  const isExpired = remaining === 0;

  return (
    <div style={{
      display: "inline-flex", alignItems: "center", gap: 8,
      padding: "6px 14px", borderRadius: 8,
      background: isExpired ? "#fdecea" : isUrgent ? "#fff8e1" : "#f0f7f0",
      border: `1px solid ${isExpired ? "#e57373" : isUrgent ? "#ffb300" : "#a5d6a7"}`,
    }}>
      <ClockCircleOutlined style={{ color: isExpired ? "#c62828" : isUrgent ? "#e65100" : "#2e7d32", fontSize: 13 }} />
      <span style={{ fontSize: 13, fontWeight: 600, color: isExpired ? "#c62828" : isUrgent ? "#e65100" : "#2e7d32", letterSpacing: "0.03em" }}>
        {isExpired
          ? "Deadline passed"
          : `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")} remaining`}
      </span>
    </div>
  );
};

// ─── Task Card (Admin) ──────────────────────────────────────────────────────
// Admin can always act on any task (uploads immediately mark it Completed —
// there's no draft concept on this endpoint), and always has an individual
// "Request Completion" nudge available per task, matching the ask for a
// per-card button rather than one global button for the whole modal.
const AdminTaskCard = ({ taskKey, task, index, clientId, onRefresh }) => {
  const [uploading, setUploading] = useState(false);
  const [file, setFile] = useState(null);
  const [requesting, setRequesting] = useState(false);
  const [revising, setRevising] = useState(false);

  const beforeUpload = (f) => {
    if (f.size > 50 * 1024 * 1024) {
      ShowToast("File must be under 50 MB", Severty.ERROR);
      return Upload.LIST_IGNORE;
    }
    setFile(f);
    return false;
  };

  const handleSubmit = async () => {
    if (!file) {
      ShowToast("Please select a file first", Severty.ERROR);
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      await axios.post(
        `${BASE_URL}${apiPath.onboarding.manualBrandTask(clientId, taskKey)}`,
        formData,
        { headers: { ...authHeaders(), "Content-Type": "multipart/form-data" } }
      );
      ShowToast("Task submitted!", Severty.SUCCESS);
      setFile(null);
      setRevising(false);
      onRefresh();
    } catch (err) {
      ShowToast(err?.response?.data?.message || "Upload failed", Severty.ERROR);
    } finally {
      setUploading(false);
    }
  };

  const handleRequestCompletion = async () => {
    setRequesting(true);
    try {
      const res = await axios.post(
        `${BASE_URL}${apiPath.onboarding.manualBrandRequest(clientId, taskKey)}`,
        {},
        { headers: authHeaders() }
      );
      ShowToast(res.data?.message || "Reminder sent", Severty.SUCCESS);
      onRefresh();
    } catch (err) {
      ShowToast(err?.response?.data?.message || "Failed to send reminder", Severty.ERROR);
    } finally {
      setRequesting(false);
    }
  };

  const isCompleted = task?.status === "completed";

  const statusTag = isCompleted ? (
    <Tag color="green" style={{ marginLeft: 0, fontSize: 11, lineHeight: "16px" }}>Completed</Tag>
  ) : (
    <Tag style={{ marginLeft: 0, fontSize: 11, lineHeight: "16px" }}>Pending</Tag>
  );

  return (
    <div style={{
      display: "flex", gap: 16, padding: "16px 18px",
      border: `1.5px solid ${isCompleted ? "#a5d6a7" : "#e8e8e8"}`,
      borderRadius: 12,
      background: isCompleted ? "#f0f9f1" : "#fff",
      marginBottom: 10,
      transition: "border-color 0.2s",
    }}>
      <div style={{
        width: 36, height: 36, borderRadius: "50%", flexShrink: 0,
        display: "flex", alignItems: "center", justifyContent: "center",
        background: isCompleted ? "#27ae60" : "#f5f5f5",
        color: isCompleted ? "#fff" : "#999",
        fontSize: isCompleted ? 18 : 14, fontWeight: 600,
      }}>
        {isCompleted ? <CheckCircleFilled style={{ fontSize: 20 }} /> : String(index + 1).padStart(2, "0")}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
          <span style={{ color: "#185250", fontSize: 15, display: "flex" }}>{TASK_ICONS[taskKey]}</span>
          <span style={{ fontWeight: 600, fontSize: 14, color: "#1a1a1a" }}>{TASK_LABELS[taskKey]}</span>
          {statusTag}
        </div>
        <div style={{ fontSize: 12, color: "#888", marginBottom: isCompleted ? 6 : 10 }}>
          {TASK_DESCRIPTIONS[taskKey]}
        </div>
        {task?.assignedTo && (
          <div style={{ fontSize: 11, color: "#185250", marginBottom: 8 }}>
            Assigned To: {task.assignedTo.name}
            {task?.assignedBy?.name && <> · Assigned By: {task.assignedBy.name}</>}
          </div>
        )}

        {isCompleted && !revising ? (
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            {task?.submittedAt && (
              <span style={{ fontSize: 12, color: "#888" }}>
                {new Date(task.submittedAt).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
              </span>
            )}
            {task?.fileUrl && (
              <a href={task.fileUrl} target="_blank" rel="noreferrer">
                <Button size="small" icon={<EyeOutlined />} style={{ fontSize: 12 }}>View file</Button>
              </a>
            )}
            <Button
              size="small"
              icon={<SwapOutlined />}
              onClick={() => setRevising(true)}
              style={{ fontSize: 12 }}
            >
              Replace / Revise
            </Button>
          </div>
        ) : (
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            {isCompleted && revising && (
              <Button
                size="small"
                onClick={() => { setRevising(false); setFile(null); }}
                style={{ fontSize: 12 }}
              >
                Cancel
              </Button>
            )}
            {task?.fileUrl && !file && (
              <a href={task.fileUrl} target="_blank" rel="noreferrer">
                <Button size="small" icon={<EyeOutlined />} style={{ fontSize: 12 }}>View current</Button>
              </a>
            )}
            <Upload
              beforeUpload={beforeUpload}
              maxCount={1}
              onRemove={() => setFile(null)}
              fileList={file ? [file] : []}
              showUploadList={!!file}
            >
              <Button size="small" icon={<UploadOutlined />} disabled={uploading} style={{ fontSize: 12 }}>
                {task?.fileUrl ? "Replace" : "Select file"}
              </Button>
            </Upload>
            <Button
              size="small"
              type="primary"
              onClick={handleSubmit}
              loading={uploading}
              disabled={!file}
              style={{ fontSize: 12, background: "#185250", borderColor: "#185250" }}
            >
              {isCompleted ? "Submit Revision" : "Submit"}
            </Button>
            <Button
              size="small"
              icon={<BellOutlined />}
              onClick={handleRequestCompletion}
              loading={requesting}
              style={{ fontSize: 12, color: "#185250", borderColor: "#185250" }}
            >
              Request Completion
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Info Row ────────────────────────────────────────────────────────────────
const InfoRow = ({ label, value, link }) => {
  if (!value) return null;
  return (
    <div style={{ display: "flex", gap: 8, padding: "7px 0", borderBottom: "1px solid #f0f0f0" }}>
      <span style={{ width: 120, flexShrink: 0, fontSize: 12, color: "#888", paddingTop: 1 }}>{label}</span>
      <span style={{ fontSize: 13, color: "#1a1a1a", fontWeight: 500 }}>
        {link ? <a href={value} target="_blank" rel="noreferrer" style={{ color: "#185250" }}>{value}</a> : value}
      </span>
    </div>
  );
};

// ─── Posting Schedule (brand-supplied preference) ─────────────────────────
// Kept in sync with brand-manager-Imergey/src/pages/onboarding/BrandOnboarding.js
// — same CSS/markup, only the API endpoint differs.
const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const blankScheduleEntry = () => ({
  assetType: "Post",
  assetTypeCustom: "",
  frequencyUnit: "week",
  frequencyCount: 1,
  frequencyCustom: "",
  daysMode: "specific",
  days: [],
  daysCustom: "",
});

const toFormEntry = (e) => ({ ...blankScheduleEntry(), ...(e || {}) });

const PostingScheduleEditor = ({ clientId, value, onSaved }) => {
  const [entries, setEntries] = useState([blankScheduleEntry()]);
  const [saving, setSaving] = useState(false);

  const seededFor = useRef(null);
  useEffect(() => {
    if (value === undefined) return;
    if (seededFor.current === clientId) return;
    seededFor.current = clientId;
    setEntries(value?.length ? value.map(toFormEntry) : [blankScheduleEntry()]);
  }, [value, clientId]);

  const updateEntry = (idx, patch) =>
    setEntries((prev) => prev.map((e, i) => (i === idx ? { ...e, ...patch } : e)));
  const removeEntry = (idx) =>
    setEntries((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev));
  const addEntry = () => setEntries((prev) => [...prev, blankScheduleEntry()]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(
        `${BASE_URL}${apiPath.onboarding.postingSchedule(clientId)}`,
        { postingSchedule: entries },
        { headers: authHeaders() }
      );
      ShowToast("Posting schedule saved", Severty.SUCCESS);
      onSaved?.();
    } catch (err) {
      ShowToast(err?.response?.data?.message || "Failed to save posting schedule", Severty.ERROR);
    } finally {
      setSaving(false);
    }
  };

  const labelStyle = { fontSize: 11, color: "#888", width: 92, flexShrink: 0, paddingTop: 6 };
  const rowStyle = { display: "flex", gap: 8, alignItems: "flex-start", marginBottom: 8 };

  return (
    <div style={{ background: "#fafafa", borderRadius: 10, padding: "12px 14px", marginBottom: 16 }}>
      <div style={{ fontSize: 12, color: "#888", marginBottom: 12, fontWeight: 600 }}>
        Posting Schedule
        <span style={{ fontWeight: 400, marginLeft: 6 }}>· set by your team</span>
      </div>

      {entries.map((entry, idx) => (
        <div
          key={idx}
          style={{
            border: "1px solid #e8e8e8",
            borderRadius: 8,
            padding: "12px 14px",
            marginBottom: 10,
            background: "#fff",
            position: "relative",
          }}
        >
          {entries.length > 1 && (
            <Button
              type="text"
              size="small"
              onClick={() => removeEntry(idx)}
              style={{ position: "absolute", top: 6, right: 6, color: "#c0392b", fontSize: 12 }}
            >
              Remove
            </Button>
          )}

          <div style={rowStyle}>
            <span style={labelStyle}>Asset Type</span>
            <div style={{ display: "flex", gap: 8, flex: 1, flexWrap: "wrap" }}>
              <Select
                size="small"
                style={{ width: 130 }}
                value={entry.assetType}
                onChange={(v) => updateEntry(idx, { assetType: v })}
                options={[
                  { label: "Post", value: "Post" },
                  { label: "Blog", value: "Blog" },
                  { label: "Custom", value: "Custom" },
                ]}
              />
              {entry.assetType === "Custom" && (
                <Input
                  size="small"
                  style={{ flex: 1, minWidth: 140 }}
                  placeholder="e.g. Newsletter, Reel"
                  value={entry.assetTypeCustom}
                  onChange={(e) => updateEntry(idx, { assetTypeCustom: e.target.value })}
                />
              )}
            </div>
          </div>

          <div style={rowStyle}>
            <span style={labelStyle}>Frequency</span>
            <div style={{ display: "flex", gap: 8, flex: 1, alignItems: "center", flexWrap: "wrap" }}>
              {entry.frequencyUnit === "custom" ? (
                <Input
                  size="small"
                  style={{ width: 150 }}
                  placeholder="e.g. every alternate day"
                  value={entry.frequencyCustom}
                  onChange={(e) => updateEntry(idx, { frequencyCustom: e.target.value })}
                />
              ) : (
                <InputNumber
                  size="small"
                  min={1}
                  style={{ width: 70 }}
                  value={entry.frequencyCount}
                  onChange={(v) => updateEntry(idx, { frequencyCount: v })}
                />
              )}
              <Select
                size="small"
                style={{ width: 120 }}
                value={entry.frequencyUnit}
                onChange={(v) => updateEntry(idx, { frequencyUnit: v })}
                options={[
                  { label: "per week", value: "week" },
                  { label: "per month", value: "month" },
                  { label: "Custom", value: "custom" },
                ]}
              />
            </div>
          </div>

          <div style={rowStyle}>
            <span style={labelStyle}>Posting Days</span>
            <div style={{ flex: 1 }}>
              <Radio.Group
                size="small"
                value={entry.daysMode}
                onChange={(e) => updateEntry(idx, { daysMode: e.target.value })}
                style={{ marginBottom: 8 }}
              >
                <Radio.Button value="specific">Specific days</Radio.Button>
                <Radio.Button value="custom">Custom / Anytime</Radio.Button>
              </Radio.Group>
              {entry.daysMode === "specific" ? (
                <Checkbox.Group
                  value={entry.days}
                  onChange={(v) => updateEntry(idx, { days: v })}
                  style={{ display: "flex", flexWrap: "wrap", gap: 4 }}
                  options={WEEKDAYS.map((d) => ({ label: d, value: d }))}
                />
              ) : (
                <Input
                  size="small"
                  style={{ width: "100%" }}
                  placeholder='e.g. "anytime"'
                  value={entry.daysCustom}
                  onChange={(e) => updateEntry(idx, { daysCustom: e.target.value })}
                />
              )}
            </div>
          </div>
        </div>
      ))}

      <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
        <Button size="small" onClick={addEntry} style={{ fontSize: 12 }}>
          + Add asset type
        </Button>
        <Button
          type="primary"
          size="small"
          loading={saving}
          onClick={handleSave}
          style={{ background: "#185250", borderColor: "#185250", fontSize: 12 }}
        >
          Save schedule
        </Button>
      </div>
    </div>
  );
};

// ─── Client Detail Modal (Admin) ───────────────────────────────────────────
const ClientDetailModal = ({ visible, onClose, clientId, clientName, onUpdated }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("profile");
  const [requestingClient, setRequestingClient] = useState(false);

  const fetchStatus = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${BASE_URL}${apiPath.onboarding.status(clientId)}`, { headers: authHeaders() });
      setData(res.data?.data);
      onUpdated?.();
    } catch {
      ShowToast("Failed to fetch onboarding details", Severty.ERROR);
    } finally {
      setLoading(false);
    }
  }, [clientId, onUpdated]);

  useEffect(() => {
    if (visible && clientId) fetchStatus();
  }, [visible, clientId, fetchStatus]);

  // Nudges the client directly — their own steps (LinkedIn, basic info,
  // questionnaire, resume), not the brand team's deliverables.
  const handleRequestClientCompletion = async () => {
    setRequestingClient(true);
    try {
      const res = await axios.post(
        `${BASE_URL}${apiPath.onboarding.resend(clientId)}`,
        {},
        { headers: authHeaders() }
      );
      ShowToast(res.data?.message || "Reminder sent to client", Severty.SUCCESS);
    } catch (err) {
      ShowToast(err?.response?.data?.message || "Failed to send reminder", Severty.ERROR);
    } finally {
      setRequestingClient(false);
    }
  };

  const ob = data?.onboarding;
  const client = ob?.client || {};
  const completedTasks = TASK_KEYS.filter(k => ob?.brandTasks?.[k]?.status === "completed").length;
  const answeredQs = ob?.steps?.questionnaire?.answers?.filter(a => {
    const ans = a.answer;
    return ans && (Array.isArray(ans) ? ans.length > 0 : ans.trim());
  }) || [];

  const profileTab = ob && (
    <div>
      {ob.onboardingStatus !== "completed" && (
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          gap: 10, marginBottom: 16, padding: "10px 14px",
          background: "#fff8e1", border: "1px solid #ffe082", borderRadius: 10,
        }}>
          <span style={{ fontSize: 12, color: "#8a6d00" }}>
            Client has not completed their onboarding steps yet.
          </span>
          <Button
            size="small"
            icon={<BellOutlined />}
            onClick={handleRequestClientCompletion}
            loading={requestingClient}
            style={{ fontSize: 12, color: "#185250", borderColor: "#185250" }}
          >
            Request Completion
          </Button>
        </div>
      )}
      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
        {ob.steps?.linkedinLogin?.status === "completed" && (
          (client.linkedinUrl || client.vanityName) ? (
            <a
              href={client.linkedinUrl || `https://www.linkedin.com/in/${client.vanityName}`}
              target="_blank"
              rel="noreferrer"
              style={{ textDecoration: "none" }}
            >
              <div style={{
                display: "inline-flex", alignItems: "center", gap: 10,
                padding: "8px 14px 8px 10px", borderRadius: 10, background: "#0a66c2", cursor: "pointer",
              }}>
                <div style={{
                  width: 26, height: 26, borderRadius: 5, background: "#fff",
                  display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                }}>
                  <LinkedinOutlined style={{ color: "#0a66c2", fontSize: 16 }} />
                </div>
                <div>
                  <div style={{ fontSize: 11, color: "rgba(255,255,255,0.7)", lineHeight: 1, marginBottom: 2 }}>
                    View profile on
                  </div>
                  <div style={{ fontSize: 13, color: "#fff", fontWeight: 600, lineHeight: 1 }}>
                    LinkedIn
                  </div>
                </div>
              </div>
            </a>
          ) : (
            <div style={{
              display: "inline-flex", alignItems: "center", gap: 8,
              padding: "7px 12px", borderRadius: 10,
              border: "1.5px solid #0a66c2", background: "#e8f0fb",
            }}>
              <LinkedinOutlined style={{ color: "#0a66c2", fontSize: 16 }} />
              <span style={{ fontSize: 12, color: "#0a66c2", fontWeight: 600 }}>LinkedIn Connected</span>
            </div>
          )
        )}
        {ob.steps?.resumeUpload?.fileUrl && (
          <a href={ob.steps.resumeUpload.fileUrl} target="_blank" rel="noreferrer">
            <Button icon={<DownloadOutlined />} size="small" style={{ fontSize: 12 }}>Download Resume</Button>
          </a>
        )}
      </div>

      {ob.steps?.basicInfo?.data && (
        <div style={{ background: "#fafafa", borderRadius: 10, padding: "8px 14px", marginBottom: 16 }}>
          <InfoRow label="Full name" value={ob.steps.basicInfo.data.fullName} />
          <InfoRow label="Company" value={ob.steps.basicInfo.data.companyName} />
          <InfoRow label="Designation" value={ob.steps.basicInfo.data.designation} />
          <InfoRow label="Industry" value={joinList(ob.steps.basicInfo.data.industry)} />
          <InfoRow label="Target Audience" value={joinList(ob.steps.basicInfo.data.audience)} />
          <InfoRow label="Geography" value={joinList(ob.steps.basicInfo.data.geography)} />
          <InfoRow label="Website" value={ob.steps.basicInfo.data.companyWebsite} link />
        </div>
      )}

      <PostingScheduleEditor
        clientId={clientId}
        value={data?.postingSchedule}
        onSaved={fetchStatus}
      />

      {ob.steps?.questionnaire?.pdfUrl ? (
        <a href={ob.steps.questionnaire.pdfUrl} target="_blank" rel="noreferrer">
          <Button icon={<DownloadOutlined />} size="small" style={{ fontSize: 12, marginBottom: 16 }}>
            View Questionnaire (PDF)
          </Button>
        </a>
      ) : (
      <Collapse ghost defaultActiveKey={answeredQs.length > 0 ? ["q"] : []}>
        <Panel
          header={
            <span style={{ fontSize: 13, fontWeight: 600, color: "#333" }}>
              Questionnaire answers
              <span style={{ marginLeft: 8, fontSize: 12, fontWeight: 400, color: "#888" }}>
                {answeredQs.length} of {Object.keys(QUESTION_MAP).length} answered
              </span>
            </span>
          }
          key="q"
        >
          {answeredQs.length === 0 ? (
            <div style={{ color: "#bbb", fontSize: 13, padding: "8px 0" }}>
              {ob.steps?.questionnaire?.status === "completed"
                ? "Client submitted the questionnaire but left all answers blank."
                : "Client has not submitted the questionnaire yet."}
            </div>
          ) : (
            answeredQs.map((a, idx) => {
              const label = QUESTION_MAP[a.id];
              if (!label) return null;
              const answer = Array.isArray(a.answer) ? a.answer.join(", ") : a.answer;
              return (
                <div key={a.id} style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 12, color: "#888", marginBottom: 3 }}>
                    Q{idx + 1}. {label}
                  </div>
                  <div style={{
                    fontSize: 13, color: "#1a1a1a",
                    background: "#f8f8f8", borderRadius: 6,
                    padding: "6px 10px", borderLeft: "3px solid #185250",
                  }}>
                    {answer}
                  </div>
                </div>
              );
            })
          )}
        </Panel>
      </Collapse>
      )}

      {!ob.steps?.basicInfo?.data && answeredQs.length === 0 && (
        <div style={{ textAlign: "center", padding: "24px 0", color: "#bbb", fontSize: 13 }}>
          Client has not filled in their profile yet.
        </div>
      )}
    </div>
  );

  const tasksTab = ob && (
    <div>
      {!ob.brandOnboardingEnabled ? (
        <div style={{ textAlign: "center", padding: "32px 0", color: "#888", fontSize: 13 }}>
          <LockOutlined style={{ fontSize: 32, color: "#ccc", display: "block", marginBottom: 10 }} />
          Brand tasks unlock once the client completes their onboarding.
        </div>
      ) : (
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
            <span style={{ fontSize: 13, color: "#888" }}>
              {completedTasks} of {TASK_KEYS.length} tasks completed
            </span>
            <Progress
              percent={Math.round((completedTasks / TASK_KEYS.length) * 100)}
              size="small"
              strokeColor="#185250"
              style={{ width: 120, margin: 0 }}
            />
          </div>
          {TASK_KEYS.map((key, idx) => (
            <AdminTaskCard
              key={key}
              taskKey={key}
              task={ob.brandTasks?.[key]}
              index={idx}
              clientId={clientId}
              onRefresh={fetchStatus}
            />
          ))}
        </div>
      )}
    </div>
  );

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      width={680}
      styles={{ body: { padding: 0 } }}
      title={null}
      style={{ top: 40 }}
    >
      {loading && (
        <div style={{ textAlign: "center", padding: 60 }}>
          <Spin size="large" />
        </div>
      )}

      {!loading && ob && (
        <>
          <div style={{
            padding: "24px 28px 18px",
            borderBottom: "1px solid #f0f0f0",
            display: "flex", alignItems: "center", gap: 14,
          }}>
            <Avatar name={client.name} image={client.image} size={52} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: 16, color: "#1a1a1a", lineHeight: 1.3 }}>
                {client.name || clientName}
              </div>
              <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>{client.email}</div>
            </div>
            {ob.brandOnboardingDeadline && ob.brandOnboardingStatus !== "completed" && (
              <CountdownTimer deadline={ob.brandOnboardingDeadline} />
            )}
          </div>

          <div style={{ display: "flex", gap: 0, borderBottom: "1px solid #f0f0f0", background: "#fafafa" }}>
            {[
              { label: "Client", value: <StatusPill status={ob.onboardingStatus} /> },
              { label: "Brand tasks", value: <StatusPill status={ob.brandOnboardingStatus} /> },
              {
                label: "Progress",
                value: (
                  <span style={{ fontSize: 13, fontWeight: 600, color: "#185250" }}>
                    {completedTasks}/{TASK_KEYS.length} done
                  </span>
                )
              },
            ].map((item, i) => (
              <div key={i} style={{
                flex: 1, padding: "12px 18px",
                borderRight: i < 2 ? "1px solid #f0f0f0" : "none",
                textAlign: "center",
              }}>
                <div style={{ fontSize: 11, color: "#aaa", marginBottom: 5, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                  {item.label}
                </div>
                {item.value}
              </div>
            ))}
          </div>

          <div style={{ padding: "0 28px 24px" }}>
            <Tabs
              activeKey={activeTab}
              onChange={setActiveTab}
              size="small"
              style={{ marginTop: 4 }}
              items={[
                {
                  key: "profile",
                  label: (
                    <span style={{ fontSize: 13 }}>
                      <UserOutlined style={{ marginRight: 5 }} />
                      Client profile
                    </span>
                  ),
                  children: <div style={{ paddingTop: 16 }}>{profileTab}</div>,
                },
                {
                  key: "tasks",
                  label: (
                    <span style={{ fontSize: 13 }}>
                      <AuditOutlined style={{ marginRight: 5 }} />
                      Brand tasks
                      {completedTasks > 0 && (
                        <span style={{
                          marginLeft: 6, background: "#185250", color: "#fff",
                          borderRadius: 10, padding: "0 6px", fontSize: 11,
                        }}>
                          {completedTasks}/{TASK_KEYS.length}
                        </span>
                      )}
                    </span>
                  ),
                  children: <div style={{ paddingTop: 16 }}>{tasksTab}</div>,
                },
                {
                  key: "activity",
                  label: (
                    <span style={{ fontSize: 13 }}>
                      <HistoryOutlined style={{ marginRight: 5 }} />
                      Activity
                    </span>
                  ),
                  children: <div style={{ paddingTop: 16 }}><AuditTrail auditLog={ob.auditLog} /></div>,
                },
              ]}
            />
          </div>
        </>
      )}

      {!loading && !ob && (
        <div style={{ textAlign: "center", padding: "40px 28px", color: "#bbb", fontSize: 13 }}>
          No onboarding record found for this client.
        </div>
      )}
    </Modal>
  );
};

// ─── Client Card ─────────────────────────────────────────────────────────────
const ClientCard = ({ client, onboarding, remainingMs, onClick, locked }) => {
  const pct = onboarding?.brandProgressPercentage || 0;
  const completedTasks = TASK_KEYS.filter(k => onboarding?.brandTasks?.[k]?.status === "completed").length;
  const completedClientSteps = CLIENT_STEP_ORDER.filter(k => onboarding?.steps?.[k]?.status === "completed").length;
  const clientPct = Math.round((completedClientSteps / CLIENT_STEP_ORDER.length) * 100);

  return (
    <div
      onClick={onClick}
      style={{
        background: "#fff",
        border: "1.5px solid #e8e8e8",
        borderRadius: 14,
        padding: "18px 20px",
        cursor: "pointer",
        opacity: locked ? 0.65 : 1,
        transition: "border-color 0.2s, box-shadow 0.2s",
      }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = "#185250"; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = "#e8e8e8"; }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
        <Avatar name={client.name} image={client.image} size={40} bg={locked ? "#bbb" : "#185250"} />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 14, color: "#1a1a1a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {client.name}
          </div>
          <div style={{ fontSize: 12, color: "#aaa", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {client.email}
          </div>
        </div>
      </div>

      <div style={{ marginBottom: locked ? 0 : 10 }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
          <span style={{ fontSize: 12, color: "#888" }}>Client tasks</span>
          <span style={{ fontSize: 12, fontWeight: 600, color: "#185250" }}>{completedClientSteps}/{CLIENT_STEP_ORDER.length}</span>
        </div>
        <Tooltip title={`${clientPct}%`}>
          <Progress percent={clientPct} size="small" strokeColor={{ "0%": "#eba35a", "100%": "#185250" }} showInfo={false} />
        </Tooltip>
      </div>

      {locked ? (
        <div style={{ display: "flex", alignItems: "center", gap: 6, color: "#bbb", fontSize: 12, marginTop: 10 }}>
          <LockOutlined style={{ fontSize: 12 }} />
          Waiting for client to complete onboarding
        </div>
      ) : (
        <>
          <div style={{ marginBottom: 10 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
              <span style={{ fontSize: 12, color: "#888" }}>Brand tasks</span>
              <span style={{ fontSize: 12, fontWeight: 600, color: "#185250" }}>{completedTasks}/{TASK_KEYS.length}</span>
            </div>
            <Tooltip title={`${pct}%`}>
              <Progress percent={pct} size="small" strokeColor={{ "0%": "#eba35a", "100%": "#185250" }} showInfo={false} />
            </Tooltip>
          </div>

          {remainingMs !== null && pct !== 100 && (
            <div style={{
              display: "flex", alignItems: "center", gap: 5, fontSize: 12,
              color: remainingMs === 0 ? "#c62828" : remainingMs < 6 * 3600000 ? "#e65100" : "#888",
            }}>
              <ClockCircleOutlined style={{ fontSize: 12 }} />
              {remainingMs === 0
                ? "Deadline passed"
                : `${Math.floor(remainingMs / 3600000)}h ${Math.floor((remainingMs % 3600000) / 60000)}m left`}
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
const OnboardingManagement = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [search, setSearch] = useState("");

  const fetchClients = useCallback(async (opts = {}) => {
    if (!opts.silent) setLoading(true);
    try {
      const res = await axios.get(`${BASE_URL}${apiPath.onboarding.cardViewClients}`, { headers: authHeaders() });
      setClients(res.data?.data?.clients || []);
    } catch {
      ShowToast("Failed to fetch clients", Severty.ERROR);
    } finally {
      if (!opts.silent) setLoading(false);
    }
  }, []);

  const refreshClientsSilently = useCallback(() => fetchClients({ silent: true }), [fetchClients]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 300 }}>
        <Spin size="large" />
      </div>
    );
  }

  const q = search.trim().toLowerCase();
  const filtered = q
    ? clients.filter(({ client }) =>
        client.name?.toLowerCase().includes(q) || client.email?.toLowerCase().includes(q)
      )
    : clients;

  const activeClients = filtered.filter(c => c.onboarding?.brandOnboardingEnabled);
  const lockedClients = filtered.filter(c => c.onboarding && !c.onboarding?.brandOnboardingEnabled);

  return (
    <div style={{ padding: "28px 32px", maxWidth: 1100 }}>
      <div style={{ marginBottom: 28, display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 12 }}>
        <div>
          <h2 style={{ fontSize: 20, fontWeight: 600, color: "#1a1a1a", margin: 0 }}>Onboarding Management</h2>
          <p style={{ fontSize: 13, color: "#888", marginTop: 4 }}>
            Track and manage every client's onboarding — client-side steps and brand deliverables.
          </p>
        </div>
        <Input
          allowClear
          prefix={<SearchOutlined style={{ color: "#bbb" }} />}
          placeholder="Search clients..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ width: 240 }}
        />
      </div>

      {filtered.length === 0 && (
        <div style={{
          textAlign: "center", padding: "48px 0", color: "#bbb",
          border: "1.5px dashed #e8e8e8", borderRadius: 14,
        }}>
          <UserOutlined style={{ fontSize: 32, display: "block", marginBottom: 10 }} />
          No clients found.
        </div>
      )}

      {activeClients.length > 0 && (
        <div style={{ marginBottom: 32 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: "#888", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 14 }}>
            Active — {activeClients.length} client{activeClients.length > 1 ? "s" : ""}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 14 }}>
            {activeClients.map(({ client, onboarding, remainingMs }) => (
              <ClientCard
                key={client._id}
                client={client}
                onboarding={onboarding}
                remainingMs={remainingMs}
                onClick={() => setSelectedClient({ id: client._id, name: client.name })}
                locked={false}
              />
            ))}
          </div>
        </div>
      )}

      {lockedClients.length > 0 && (
        <div>
          <div style={{ fontSize: 12, fontWeight: 600, color: "#bbb", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 14 }}>
            Waiting for client — {lockedClients.length} client{lockedClients.length > 1 ? "s" : ""}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 14 }}>
            {lockedClients.map(({ client, onboarding }) => (
              <ClientCard
                key={client._id}
                client={client}
                onboarding={onboarding}
                remainingMs={null}
                onClick={() => setSelectedClient({ id: client._id, name: client.name })}
                locked
              />
            ))}
          </div>
        </div>
      )}

      {selectedClient && (
        <ClientDetailModal
          visible={!!selectedClient}
          onClose={() => setSelectedClient(null)}
          clientId={selectedClient.id}
          clientName={selectedClient.name}
          onUpdated={refreshClientsSilently}
        />
      )}
    </div>
  );
};

export default OnboardingManagement;
